import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import moment from "moment";
import { Link } from "react-router-dom";
import { ScrollableContent } from "../../Styles/InputStyles";
import { getModeName } from "../../../helpers";

const ViewReserveModal = ({ setViewDetail, selectedData }) => {
    const onClose = () => setViewDetail(false);

    return (
        <Container>
            {/* Header */}
            <Header>
                <Title>View Details</Title>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            {/* Top Section */}
            <ProfileSection className="flex justify-between">
                <UserInfo>
                    <span className="font-medium mb-0 text-lg">
                        {selectedData?.name}
                    </span>
                    <span className="text-sm text-gray-600">#{selectedData?.roomNum}, {selectedData?.propertyName}</span>
                </UserInfo>
                <div className="flex flex-col gap-0">
                    <span className='text-gray-500 text-sm'>{'Rent'}</span>
                    <span className='text-xl font-semibold text-gray-700'>{'₹' + selectedData?.rent}</span>
                </div>
            </ProfileSection>

            {/* Content */}
            <Content>
                <ScrollableContent>
                    <Wrapper>
                        {/* Basic Information */}
                        <BtnWrapper>
                            <SectionTitle>Basic Information</SectionTitle>
                        </BtnWrapper>
                        <Section>
                            <Row>
                                <FlexCol>
                                    <Label>Mobile</Label>
                                    <Value>#{selectedData?.mobile}</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Occupation</Label>
                                    <Value>{selectedData?.occupation}</Value>
                                </FlexCol>
                            </Row>

                            <Row>
                                <FlexCol>
                                    <Label>Gender</Label>
                                    <Value>{selectedData?.gender === 1 ? 'Male' : selectedData?.gender === 2 ? 'Female' : 'Other'}</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Floor/Flat</Label>
                                    <Value>{selectedData?.flatName}</Value>
                                </FlexCol>
                            </Row>

                            <Row>
                                <FlexCol>
                                    <Label>KYC Status</Label>
                                    <Value className={(selectedData?.kycStatus === 5 || selectedData?.kycStatus === 4) ? 'text-green-600' : 'text-amber-600'}>
                                        {(selectedData?.kycStatus === 5 || selectedData?.kycStatus === 4) ? 'Verified' : 'Pending'}
                                    </Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Police Verified</Label>
                                    <Value className={selectedData?.isPoliceVerified === 1 ? 'text-green-600' : 'text-red-600'}>
                                        {selectedData?.isPoliceVerified === 1 ? 'Yes' : 'No'}
                                    </Value>
                                </FlexCol>
                            </Row>
                        </Section>

                        {/* Agreement Details */}
                        <BtnWrapper>
                            <SectionTitle>Agreement Details</SectionTitle>
                        </BtnWrapper>
                        <Section>
                            <Row>
                                <FlexCol>
                                    <Label>Agreement Period</Label>
                                    <Value>{selectedData?.agreementPeriod || '0'} months</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Rental Cycle</Label>
                                    <Value>{selectedData?.rentalCycle}</Value>
                                </FlexCol>
                            </Row>

                            <Row>
                                <FlexCol>
                                    <Label>Agreement Start Date</Label>
                                    <Value>
                                        {selectedData?.agreementStartDate
                                            ? moment(selectedData?.agreementStartDate).format("DD MMM YYYY")
                                            : '-'}
                                    </Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Move-in Date</Label>
                                    <Value>
                                        {selectedData?.moveInDate
                                            ? moment(selectedData?.moveInDate).format("DD MMM YYYY")
                                            : '-'}
                                    </Value>
                                </FlexCol>
                            </Row>

                            <Row>

                                <FlexCol>
                                    <Label>Rent Agreement Signed</Label>
                                    <Value className={selectedData?.isRentAgreementSigned === 1 ? 'text-green-600' : 'text-red-600'}>
                                        {selectedData?.isRentAgreementSigned === 1 ? 'Yes' : 'No'}
                                    </Value>
                                </FlexCol>
                            </Row>
                        </Section>

                        {/* Financial Details */}
                        <BtnWrapper>
                            <SectionTitle>Financial Details</SectionTitle>
                        </BtnWrapper>
                        <Section>
                            <Row>
                                <FlexCol>
                                    <Label>Monthly Rent</Label>
                                    <Value>₹{selectedData?.rent}</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Security Deposit</Label>
                                    <Value>₹{selectedData?.security || '0'}</Value>
                                </FlexCol>
                            </Row>

                            <Row>
                                <FlexCol>
                                    <Label>Booking Amount</Label>
                                    <Value>₹{selectedData?.bookingAmt || '0'}</Value>
                                </FlexCol>
                                <FlexCol>
                                    <Label>Total Dues</Label>
                                    <Value className="text-red-600 font-semibold">₹{selectedData?.totalDues || '0'}</Value>
                                </FlexCol>
                            </Row>

                            <Row>
                                <FlexCol>
                                    <Label>Due Date</Label>
                                    <Value>
                                        {selectedData?.dueDate
                                            ? moment(selectedData?.dueDate).format("DD MMM YYYY")
                                            : 'Not set'}
                                    </Value>
                                </FlexCol>
                            </Row>
                        </Section>
                    </Wrapper>
                </ScrollableContent>
            </Content>
        </Container>
    );
};

export default ViewReserveModal;

// ---------------- Styled Components ----------------
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const Wrapper = tw.div`grid gap-2 my-0`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Content = tw.div`p-4 flex flex-col gap-4`;
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-white`;
const Section = tw.div`border border-gray-200 p-2 rounded-lg mb-3`;
const SectionTitle = tw.h2`text-base font-medium text-gray-600 flex justify-between items-center`;
const Row = tw.div`grid grid-cols-2 py-1 md:p-1 gap-2`;
const Label = tw.label`text-sm text-gray-400 font-normal`;
const Value = tw.span`text-gray-700 text-md break-all`;
const BtnWrapper = tw.div`w-full flex items-center justify-between`;
const FlexCol = tw.div`flex flex-col gap-0.5`;
const UserInfo = tw.div`flex flex-col`;
const ProfileSection = tw.div`flex items-center gap-3 bg-gray-100 px-4 p-4`;